export type DesktopTheme = "dark" | "light"

export type DesktopNotifyPayload = {
  title: string
  body?: string
}

export type DesktopBridge = {
  notify: (payload: DesktopNotifyPayload) => Promise<void>
  isFocused: () => Promise<boolean>
  setTheme: (theme: DesktopTheme) => Promise<void>
  /** Prefetch media URLs into the Electron disk cache (fire-and-forget). */
  cacheMedia?: (urls: string[]) => Promise<void>
  /** In-flight disk downloads (queued and active). */
  mediaCacheSnapshot?: () => Promise<unknown>
  /** Subscribe to disk-download progress. Returns unsubscribe. */
  subscribeMediaCacheProgress?: (listener: (event: unknown) => void) => () => void
}

declare global {
  interface Window {
    desktop?: DesktopBridge
  }
}

export function getDesktopBridge(): DesktopBridge | undefined {
  if (typeof window === "undefined") return undefined
  return window.desktop
}

export function isDesktopApp(): boolean {
  return getDesktopBridge() !== undefined
}

/** Inertia Link prefetch — только в Electron, на сайте как сейчас (без prefetch). */
export function desktopInertiaPrefetch(): "hover" | undefined {
  return isDesktopApp() ? "hover" : undefined
}

export function applyDesktopChrome(): void {
  if (typeof document === "undefined") return
  document.documentElement.classList.toggle("frameriver-desktop", isDesktopApp())
}

export function syncDesktopWindowAppearance(theme: DesktopTheme): void {
  applyDesktopChrome()
  const desktop = getDesktopBridge()
  if (!desktop?.setTheme) return
  void desktop.setTheme(theme)
}

/** Native notification only when the Electron window is in the background. */
export async function notifyDesktopIfBackground(payload: DesktopNotifyPayload): Promise<void> {
  const desktop = getDesktopBridge()
  if (!desktop) return

  try {
    if (!document.hidden) {
      const focused = await desktop.isFocused()
      if (focused) return
    }
    await desktop.notify(payload)
  } catch {
    // Missing preload or IPC failure must not break job lifecycle.
  }
}

/** Ask Electron to download generation results into the local media cache. */
export function cacheDesktopMedia(urls: Array<string | null | undefined>): void {
  const desktop = getDesktopBridge()
  if (!desktop?.cacheMedia) return

  const cleaned = Array.from(new Set(urls.map((u) => u?.trim()).filter((u): u is string => Boolean(u))))
  if (cleaned.length === 0) return

  try {
    void desktop.cacheMedia(cleaned)
  } catch {
    // IPC failure must not break job lifecycle.
  }
}
